import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { take, tap } from "rxjs/operators";
import { IEngineerJobBookingWithEngineerMappings } from "../foundation.interfaces";
import { IEngineerJobBookingMapping } from "../_models/IEngineerJobBookingMapping";
import { IUserJobBookingMappingQueryOptions } from "../_models/IUserJobBookingMappingQueryOptions";

@Injectable({
    providedIn: "root"
})
export class EngineerJobBookingMappingService {

    jobBookingMappingListsSubject = new Subject<{ [jobBookingId: number]: IEngineerJobBookingMapping[] }>();
    private jobBookingMappingLists: { [jobBookingId: number]: IEngineerJobBookingMapping[] } = {};
    constructor(private readonly http: HttpClient) { }

    getEngineerJobBookingMappings(options: IUserJobBookingMappingQueryOptions = null): Observable<IEngineerJobBookingMapping[]> {
        return this.http.get<IEngineerJobBookingMapping[]>(`/api/foundation/engineerbookings/mappings`, { params: options as HttpParams }).pipe(tap(mappings => {
            mappings.forEach(mapping => {
                this.addMappingToCachedJobBookingMappingList(mapping);
            });
            this.jobBookingMappingListsSubject.next(this.jobBookingMappingLists);
        }))
    }

    addUpdateEngineerJobBookingMapping(mapping: IEngineerJobBookingMapping): Observable<IEngineerJobBookingMapping> {
        return this.http.post<IEngineerJobBookingMapping>(`/api/foundation/engineerbookings/mappings`, mapping).pipe(tap(mapping => {
            this.addMappingToCachedJobBookingMappingList(mapping);
        }));
    }

    createUpdateJobBookingMappings(booking: IEngineerJobBookingWithEngineerMappings): Observable<IEngineerJobBookingMapping>[] {
        let mappingObservables: Observable<IEngineerJobBookingMapping>[] = [];

        booking.assignedEngineers.forEach(engineer => {
            if (!booking.mappings.find(mapping => mapping.UserId === engineer.Id)) {
                mappingObservables.push(this.createJobBookingMappingsFromEngineerId(engineer.Id, booking.Id).pipe(take(1)));
            }
        })
        return mappingObservables;
    }

    deleteJobBookingMapping(mapping: IEngineerJobBookingMapping): Observable<any> {
        this.removeMappingFromCachedBookingMappingLists(mapping);
        return this.http.delete<IEngineerJobBookingMapping>(`/api/foundation/engineerbookings/mappings`, { params: { mappingId: mapping.Id } });
    }

    createJobBookingMappingsFromEngineerId(engineerId: number, jobBookingId: number): Observable<IEngineerJobBookingMapping> {
        const mapping: IEngineerJobBookingMapping = {
            Id: 0,
            EngineerJobBookingId: jobBookingId,
            UserId: engineerId
        }

        return this.addUpdateEngineerJobBookingMapping(mapping);
    }

    //--

    private addMappingToCachedJobBookingMappingList(mapping: IEngineerJobBookingMapping): void {
        const mappings = this.getCachedJobBookingMappingListsForJobBookingId(mapping.EngineerJobBookingId);

        if (!mappings?.length) {
            this.jobBookingMappingLists[mapping.EngineerJobBookingId] = [mapping];
            return;
        }

        const existing = mappings.findIndex(bk => bk.Id === mapping.Id);
        if (existing < 0) {
            mappings.push(mapping);
            return;
        }

        mappings.splice(existing, 1);
        mappings.push(mapping);
    }

    private removeMappingFromCachedBookingMappingLists(mapping: IEngineerJobBookingMapping): void {

        const mappings = this.getCachedJobBookingMappingListsForJobBookingId(mapping.EngineerJobBookingId);
        const indexToRemove = mappings.findIndex(mp => mapping.Id === mp.Id);
        mappings.splice(indexToRemove, 1);
    }

    private getCachedJobBookingMappingListsForJobBookingId(jobBookingId): IEngineerJobBookingMapping[] {
        return this.jobBookingMappingLists[jobBookingId];
    }
}
